import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { HambergerMenu } from "iconsax-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  fetchQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
} from "../../../redux/Slice/Settings/quickRepliesSlice";
import { useOrderedQuickReplies, saveQuickReplyOrder } from "../../../utils/quickReplyOrder";
import AuthView from "../../../utils/AuthView";

// One draggable row — drag listeners live only on the handle icon, so the
// Edit/Delete buttons and the rest of the row keep working exactly as before.
const SortableQuickReplyRow = ({ reply, onEdit, onDeleteRequest, canEdit, canDelete }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: reply._id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.5,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        zIndex: isDragging ? 1 : "auto",
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          mt: 0.25,
          cursor: "grab",
          color: "text.secondary",
          touchAction: "none",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <HambergerMenu size="18" variant="Linear" />
      </Box>
      <FlashOnIcon sx={{ color: "primary.main", mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          /{reply.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {reply.message}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
        {canEdit && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(reply)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDeleteRequest(reply._id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const EMPTY_FORM = { name: "", message: "" };

const QuickReplies = () => {
  const dispatch = useDispatch();
  const workspaceId = useSelector(
    (s) => s.auth?.user?.workspaceId || s.workspace?.currentWorkspace?._id
  );
  const { items: rawItems, loading } = useSelector((state) => state.quickReplies);
  // Applies the client-only saved drag order — see utils/quickReplyOrder.js
  // (no backend `order` field yet, per Rahim's explicit "strictly UI/UX" call
  // 2026-08-24 — order persists per-browser via localStorage only).
  const items = useOrderedQuickReplies(rawItems, workspaceId);
  const qrPerms = useSelector(
    (s) => s.userDetails?.userPermissions?.permissions?.QuickReplies || {}
  );
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !workspaceId) return;
    const oldIndex = items.findIndex((r) => r._id === active.id);
    const newIndex = items.findIndex((r) => r._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    saveQuickReplyOrder(workspaceId, reordered.map((r) => r._id));
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = update
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchQuickReplies(workspaceId));
    }
  }, [dispatch, workspaceId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (reply) => {
    setEditing(reply);
    setForm({ name: reply.name, message: reply.message });
    setError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError("Both name and message are required.");
      return;
    }
    if (!workspaceId) {
      setError("No workspace found. Please select a workspace first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing) {
        await dispatch(updateQuickReply({
          workspaceId,
          replyId: editing._id,
          name: form.name.trim(),
          message: form.message,
        })).unwrap();
      } else {
        await dispatch(createQuickReply({
          workspaceId,
          name: form.name.trim(),
          message: form.message,
        })).unwrap();
      }
      closeDialog();
    } catch (err) {
      setError(err || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (replyId) => {
    if (!workspaceId) return;
    await dispatch(deleteQuickReply({ workspaceId, replyId }));
    setDeleteConfirmId(null);
  };

  if (!qrPerms.View) {
    return <AuthView />;
  }

  return (
    <Box
      sx={{
        padding: "20px",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        color: "text.primary",
      }}
    >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ fontSize: { xs: "1.125rem", sm: "1.375rem", md: "1.5rem" }, fontWeight: 600 }}
          >
            Quick Replies
          </Typography>
          {qrPerms.Add && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              size="small"
            >
              Add
            </Button>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Type <strong>/</strong> in the message box to search and insert a quick reply. Drag the{" "}
          <HambergerMenu size="12" variant="Linear" style={{ verticalAlign: "middle" }} /> handle to reorder.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 1.5,
              color: "text.secondary",
            }}
          >
            <FlashOnIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="body2">No quick replies yet. Click Add to create one.</Typography>
          </Box>
        ) : (
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((r) => r._id)} strategy={verticalListSortingStrategy}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {items.map((reply) => (
                  <SortableQuickReplyRow
                    key={reply._id}
                    reply={reply}
                    onEdit={openEdit}
                    onDeleteRequest={setDeleteConfirmId}
                    canEdit={qrPerms.Edit}
                    canDelete={qrPerms.Delete}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: "background.paper", color: "text.primary" } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editing ? "Edit Quick Reply" : "New Quick Reply"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <TextField
            label="Name"
            placeholder="e.g. greeting"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            size="small"
            helperText={`Type /${form.name || "name"} in the chat to use this reply`}
          />
          <TextField
            label="Message"
            placeholder="Type the full message here…"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        PaperProps={{ sx: { backgroundColor: "background.paper", color: "text.primary" } }}
      >
        <DialogTitle>Delete Quick Reply</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this quick reply?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirmId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickReplies;
